import { useEffect, useState } from 'react'
import classNames from 'classnames'
import registrationStyles from 'features/registration/Registration.module.scss'
import newSimulationCardStyles from 'features/registration/components/registration-side-card/RegistrationSideCard.module.scss'
import producerCardLayoutStyles from 'components/simulation-registration-side-card-layout/SimulationRegistrationSideCardLayout.module.scss'
import styles from './RegistrationLoading.module.scss'
import { useRouter } from 'next/router'
import { routes } from 'utils/routes'
import useWindowSize from 'hooks/useWindowSize'
import { determineSimulationUsage, requestAndSavePropositions } from 'features/simulation/utils'
import { CustomerFlows, InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { selectProposition, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import useSelectInputGroup from 'hooks/useSelectInputGroup'
import { setSelectedProducer } from 'store/app/slice'
import { useGetProducersQuery } from 'store/api/bolt-api'
import { filterAndSortProducers } from 'utils/producers'
import { MeterUsage } from 'features/simulation/types'
import { log } from 'utils/logging'
import { Flow } from 'types/logging'
import { RegistrationLoadingProps } from 'features/registration-loading/types'
import { Producer } from 'types/producer'
import FlowHeader from 'components/flow-header/FlowHeader'
import { PRODUCTS, SIMULATION_TYPES, USAGE_GRADES } from '@boltenergy-be/shared'
import { UrlSearchParams } from 'types/url'
import { getTotalElectricityVolumeForMeterType } from 'utils/usage'
import { useGetLanguage } from 'hooks/useGetLanguage'

const RegistrationLoading = ({ overrideActions, hideHeader }: RegistrationLoadingProps) => {
  // Redux store
  const simulation = useSelectInputGroup(InputGroupKeys.SIMULATION)
  const { simulationType } = useStoreSelector((store) => store.marketing)
  const { opportunityId } = useStoreSelector((store) => store.app)
  const dispatch = useStoreDispatch()

  // Next router
  const router = useRouter()
  const language = useGetLanguage()

  // Window size
  const { isTablet } = useWindowSize()

  // Local state
  const [failedAttempts, setFailedAttempts] = useState<number>(0)

  // Redux queries
  const { data: producers } = useGetProducersQuery()

  /**
   * Loads the express registration data & redirects to registration
   */
  const loadExpressRegistrationData = async (attempts: number) => {
    // Redirect to home after 3 failed attempts
    if (attempts > 3) {
      overrideActions ? overrideActions.error() : router.push(routes(language).home)
    }

    const usage = determineSimulationUsage(USAGE_GRADES.average)
    dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.USAGE, value: usage }))

    // Fetch producerSlug
    const producerSlug = await fetchProducerSlug(usage)

    // Request propositions simultaneously
    await requestAndSavePropositions({
      onError: () => setFailedAttempts(attempts + 1),
      onSuccess: () => {
        // Set the selected proposition
        dispatch(selectProposition({ productType: PRODUCTS.variableOffline }))

        // Redirect to registration
        if (producerSlug) {
          overrideActions ? overrideActions.success() : router.replace(routes(language, producerSlug).registration)
        } else {
          log({ error: 'No producerSlug found', identifier: `[${Flow.REGISTRATION}:loadExpressRegistration]` })
          overrideActions ? overrideActions.error() : router.replace(routes(language).home)
        }
      }
    })
  }

  /**
   * Fetches & returns the producer slug
   *
   * @returns {Promise<string>} The producer slug
   */
  const fetchProducerSlug = async (usage?: MeterUsage): Promise<string> => {
    const availableProducers = filterAndSortProducers({
      capacity: usage ? getTotalElectricityVolumeForMeterType(simulation[SimulationFieldKeys.METER_TYPE], usage) : undefined,
      hideSoldOut: true,
      language,
      producers: Object.values(producers ?? {})
    })

    const selectedProducer: Producer = availableProducers[0]

    dispatch(setSelectedProducer({ producerSlug: selectedProducer.slug }))
    return selectedProducer.slug
  }

  /**
   * Triggered when failedAttempts or simulationType changes
   * If simulationType = "Express" then loadExpressRegistrationData is called
   */
  useEffect(() => {
    const opportunityIdToUse = opportunityId || new URLSearchParams(window.location.search).get(UrlSearchParams.OPPORTUNITY_ID)
    const fetchSimulation = new URLSearchParams(window.location.search).get('fetchSimulation') === 'true'

    if (producers && Object.keys(producers)?.length) {
      if (!opportunityIdToUse && simulation.chosenSimulationType === SIMULATION_TYPES.express) loadExpressRegistrationData(failedAttempts)
      if (
        simulation.chosenSimulationType !== SIMULATION_TYPES.boltGo &&
        simulation.chosenSimulationType !== SIMULATION_TYPES.express &&
        !fetchSimulation
      ) {
        overrideActions ? overrideActions.error() : router.replace(routes(language).home)
      }
    }
  }, [producers, opportunityId, failedAttempts, simulationType])

  return (
    <>
      {!hideHeader && <FlowHeader flow={CustomerFlows.REGISTRATION} />}

      <div className={registrationStyles.registration}>
        <div className="container">
          <div className={classNames('is-flex-row', 'is-flex-centered-direction', registrationStyles.wrapper)}>
            <div className={registrationStyles.content}>
              <div className={classNames(registrationStyles.box, styles.form)}>
                <div className={styles.line} />
                <div className={styles.line} />
                <div className={styles.line} />
              </div>
            </div>

            {!isTablet && (
              <div className={newSimulationCardStyles.container}>
                <div className={producerCardLayoutStyles.card}>
                  <div className={classNames(producerCardLayoutStyles['card-header'], styles.image)} />

                  <div className={producerCardLayoutStyles['card-content-container']}>
                    <section className={styles['skeleton-lines-layout']}>
                      <div className={styles.line} />
                      <div className={styles.line} />
                      <div className={styles.line} />
                    </section>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default RegistrationLoading
